import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCcw } from 'lucide-react';
import { api } from '../../lib/api';

// "Can I send this school the link?" — runs every new-deploy check in one call
// and shows the fix next to each failure. The check also repairs the page map,
// so nobody needs to press "Scan sitemap" for a new deploy.
//
// Runs on its own for temporary (*.pages.dev) sites, since those are the ones
// in review; launched sites check on demand.

const TONE = {
  pass: { Icon: CheckCircle2, color: 'text-[#B0FC38]', label: 'Passed' },
  warn: { Icon: AlertTriangle, color: 'text-amber-300', label: 'Needs a look' },
  fail: { Icon: XCircle, color: 'text-red-300', label: 'Failed' },
};

function isTemporarySite(siteUrl) {
  try { return /\.pages\.dev$/i.test(new URL(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`).hostname); } catch { return false; }
}

export default function SiteReadinessCard({ account, onChecked }) {
  const [readiness, setReadiness] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const autoRan = useRef(null);

  const run = useCallback(async () => {
    setRunning(true);
    setError('');
    try {
      const res = await api.post(`/api/admin/accounts/${account.id}/site-readiness`, {});
      setReadiness(res.readiness);
      onChecked?.(res.readiness);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'The readiness check could not run.');
    } finally {
      setRunning(false);
    }
  }, [account.id, onChecked]);

  useEffect(() => {
    if (!account.siteUrl || !isTemporarySite(account.siteUrl)) return;
    if (autoRan.current === account.id) return;
    autoRan.current = account.id;
    run();
  }, [account.id, account.siteUrl, run]);

  if (!account.siteUrl) return null;

  let headline = 'Check that the site is ready to hand to the school.';
  if (running && !readiness) headline = 'Checking the site…';
  else if (readiness?.ready && readiness.warned === 0) headline = 'Ready to send to the school.';
  else if (readiness?.ready) headline = `Ready, with ${readiness.warned} item${readiness.warned === 1 ? '' : 's'} to look at before launch.`;
  else if (readiness) headline = `Not ready: ${readiness.failed} item${readiness.failed === 1 ? '' : 's'} to fix.`;

  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5" data-testid="site-readiness">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="text-[13px] font-semibold text-white">Site readiness</h3>
        <button
          type="button"
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] bg-white/[0.04] border border-white/[0.08] text-gray-200 hover:bg-white/[0.08] disabled:opacity-50"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
          {running ? 'Checking…' : readiness ? 'Check again' : 'Check now'}
        </button>
      </div>

      <p
        role="status"
        data-testid="site-readiness-headline"
        className={`text-[13px] mb-3 ${readiness && !readiness.ready ? 'text-red-200' : readiness?.ready ? 'text-[#B0FC38]' : 'text-gray-400'}`}
      >
        {headline}
      </p>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {readiness && (
        <ul className="space-y-2">
          {readiness.checks.map((c) => {
            const tone = TONE[c.status] || TONE.warn;
            return (
              <li key={c.key} data-testid={`readiness-${c.key}`} data-status={c.status} className="flex items-start gap-2.5 py-1.5 border-b border-white/[0.03] last:border-b-0">
                <tone.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone.color}`} aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-[13px] text-gray-200">
                    {c.label} <span className="sr-only">— {tone.label}</span>
                  </div>
                  <div className="text-[12px] text-gray-500 mt-0.5 break-words">{c.detail}</div>
                  {c.fix && c.status !== 'pass' && (
                    <div className="text-[12px] text-gray-300 mt-1 break-words"><span className="text-gray-500">Fix: </span>{c.fix}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
